import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Search, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { qualityNcrAPI } from '@/lib/api';
import { ReasonSelect, useReasons, fmtDate } from './NcrShared';

const today = () => new Date().toLocaleDateString('en-CA');

const DispatchPicker = ({ picked, onPick }) => {
  const [q, setQ] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [unreco, setUnreco] = useState(false);
  const search = async (inc = unreco) => {
    setLoading(true);
    try { setRows((await qualityNcrAPI.dispatchSearch(q, inc)).data); } catch { toast.error('Search failed'); } finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { search(); }, []);
  return (
    <div className="space-y-2">
      <Label className="text-xs">1 · Dispatch document (DC no., customer, part no. or WO)</Label>
      <div className="flex gap-2">
        <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && search()} placeholder="Search dispatch challans" data-testid="complaint-dispatch-search-input" />
        <Button variant="outline" onClick={() => search()} data-testid="complaint-dispatch-search-btn">{loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}</Button>
      </div>
      <div className="flex items-center gap-2">
        <Switch checked={unreco} onCheckedChange={(v) => { setUnreco(v); search(v); }} data-testid="complaint-include-unreco-switch" />
        <Label className="text-xs font-normal">Also show non-reconciled DCs (trial / without PO)</Label>
      </div>
      <div className="max-h-56 overflow-y-auto border rounded divide-y" data-testid="complaint-dispatch-results">
        {rows.length === 0 && <p className="text-xs text-muted-foreground p-3">No dispatch documents found</p>}
        {rows.map((dc) => (
          <div key={dc.id} className="p-2 text-xs">
            <div className="font-medium">{dc.dc_number} <span className="text-muted-foreground font-normal">· {dc.customer_name} · {fmtDate(dc.dispatch_date)}{dc.invoice_number ? ` · Inv ${dc.invoice_number}` : ''}</span>
              {dc.reconciliation_status !== 'reconciled' && <span className="ml-2 rounded bg-amber-100 text-amber-800 px-1.5 py-0.5 text-[10px]" data-testid={`complaint-dc-unreco-${dc.id}`}>Not reconciled</span>}</div>
            {dc.items.map((it) => {
              const active = picked?.dc.id === dc.id && picked?.line.line_index === it.line_index;
              return (
                <button key={it.line_index} onClick={() => onPick({ dc, line: it })}
                  className={`mt-1 w-full text-left rounded px-2 py-1 border ${active ? 'border-teal-500 bg-teal-50' : 'border-transparent hover:bg-muted'}`}
                  data-testid={`complaint-dispatch-line-${dc.id}-${it.line_index}`}>
                  <span className="font-mono">{it.item_unique_id}</span> {it.item_name} — qty {it.quantity}{it.wo_number ? ` · ${it.wo_number}` : ''}
                </button>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
};

export const NewComplaintDialog = ({ open, onClose, onCreated }) => {
  const reasons = useReasons();
  const [sources, setSources] = useState([]);
  const [picked, setPicked] = useState(null);
  const [f, setF] = useState({ reported_date: today(), source: '', quantity: '', reason: '', problem: '', remarks: '' });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!open) return;
    qualityNcrAPI.complaintMeta().then((r) => setSources(r.data.sources)).catch(() => {});
    setPicked(null); setF({ reported_date: today(), source: '', quantity: '', reason: '', problem: '', remarks: '' });
  }, [open]);
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
  const submit = async () => {
    setSaving(true);
    try {
      const res = await qualityNcrAPI.createComplaint({ ...f, quantity: parseFloat(f.quantity), dispatch_challan_id: picked.dc.id, line_index: picked.line.line_index });
      toast.success(`Complaint ${res.data.number} recorded`); onCreated(res.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to record complaint'); } finally { setSaving(false); }
  };
  const ready = picked && f.source && f.quantity && f.reason && f.problem.trim() && f.reported_date;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="new-complaint-dialog">
        <DialogHeader>
          <DialogTitle>Record customer complaint / external rejection</DialogTitle>
          <DialogDescription>Link it to the dispatch document — the work order, raw-material challan and purchased lots are traced automatically.</DialogDescription>
        </DialogHeader>
        <DispatchPicker picked={picked} onPick={(p) => { setPicked(p); setF((x) => ({ ...x, quantity: x.quantity || String(p.line.quantity) })); }} />
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs">2 · Complaint reported date</Label><Input type="date" max={today()} value={f.reported_date} onChange={(e) => set('reported_date')(e.target.value)} data-testid="complaint-date-input" /></div>
          <div><Label className="text-xs">Source of complaint</Label>
            <Select value={f.source} onValueChange={set('source')}>
              <SelectTrigger data-testid="complaint-source-select"><SelectValue placeholder="Select source" /></SelectTrigger>
              <SelectContent>{sources.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><Label className="text-xs">Rejected qty{picked ? ` (dispatched ${picked.line.quantity})` : ''}</Label>
            <Input type="number" min="0" value={f.quantity} onChange={(e) => set('quantity')(e.target.value)} data-testid="complaint-qty-input" /></div>
          <div><Label className="text-xs">Rejection reason</Label><ReasonSelect value={f.reason} onChange={set('reason')} reasons={reasons} testid="complaint-reason-select" /></div>
        </div>
        <div><Label className="text-xs">Problem (as reported by customer)</Label><Textarea rows={3} value={f.problem} onChange={(e) => set('problem')(e.target.value)} data-testid="complaint-problem-input" /></div>
        <div><Label className="text-xs">Remarks (snag sheet / IDN no. etc.)</Label><Input value={f.remarks} onChange={(e) => set('remarks')(e.target.value)} data-testid="complaint-remarks-input" /></div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="complaint-cancel-btn">Cancel</Button>
          <Button onClick={submit} disabled={!ready || saving} data-testid="complaint-submit-btn">{saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Record complaint</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
